import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {FiBarChart2, FiPlus, FiEdit2, FiTrash2, FiSearch, FiStar } from 'react-icons/fi';
import { getCompanies, createCompany, updateCompany, deleteCompany } from '../services/companies';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import TourCompanyForm from '../components/TourCompanies/TourCompanyForm';
import toast from 'react-hot-toast';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

export default function TourCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [filters, setFilters] = useState({ status: '', service_type: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCompanies();
  }, [filters]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const data = await getCompanies(filters);
      setCompanies(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch tour companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (companyData) => {
    try {
      await createCompany(companyData);
      toast.success('Tour company created successfully');
      setShowForm(false);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to create tour company');
    }
  };

  const handleUpdate = async (id, companyData) => {
    try {
      await updateCompany(id, companyData);
      toast.success('Tour company updated successfully');
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update tour company');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tour company?')) return;
    try {
      await deleteCompany(id);
      toast.success('Tour company deleted successfully');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete tour company');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tour Companies</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> Add Company
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Service Type</label>
            <select
              value={filters.service_type}
              onChange={(e) => setFilters({ ...filters, service_type: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="safari">Safari</option>
              <option value="diving">Diving</option>
              <option value="horse">Horse Riding</option>
              <option value="boat">Boat Trips</option>
              <option value="cultural">Cultural Tours</option>
              <option value="luxury">Luxury</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Company name or contact person"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Types</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Areas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{company.name}</p>
                        <p className="text-xs text-gray-500">{company.slug}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm">{company.contact?.person || '-'}</p>
                        <p className="text-xs text-gray-500">{company.contact?.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {company.service_types?.slice(0, 2).map((type, idx) => (
                          <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {company.service_areas?.slice(0, 2).map((area, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                            {area.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {company.rating?.average > 0 ? (
                        <div className="flex items-center text-sm text-yellow-500">
                          <FiStar className="w-3 h-3 fill-current mr-1" />
                          <span>{company.rating.average}</span>
                          <span className="text-gray-400 ml-1">({company.rating.count})</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[company.status]}`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                        <button
  onClick={() => navigate(`/tour-companies/${company._id}/stats`)}
  className="text-purple-500 hover:text-purple-700 mr-3"
  title="View Stats"
>
  <FiBarChart2 className="w-4 h-4" />
</button>
                      <button
                        onClick={() => setEditingCompany(company)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(company._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {companies.length === 0 && (
            <div className="text-center py-12 text-gray-500">No tour companies found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Tour Company Form Modal */}
      <TourCompanyForm
        isOpen={showForm || !!editingCompany}
        onClose={() => {
          setShowForm(false);
          setEditingCompany(null);
        }}
        onSubmit={editingCompany ? (data) => handleUpdate(editingCompany._id, data) : handleCreate}
        initialData={editingCompany}
      />
    </div>
  );
}
